import React from "react";
import { AlertOctagon, AlertTriangle, AlertCircle, Info, ShieldCheck } from "lucide-react";

export type Severity = "critical" | "high" | "medium" | "low" | "info";

export function normalizeSeverity(severity: string | null | undefined): Severity {
  const sev = String(severity || "").toLowerCase();
  if (sev === "critical" || sev === "high" || sev === "medium" || sev === "low" || sev === "info") {
    return sev;
  }
  return "info";
}

interface SeverityBadgeProps {
  severity: Severity;
  showIcon?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const severityConfig: Record<
  Severity,
  { label: string; icon: React.ElementType; classes: string }
> = {
  critical: {
    label: "Critical",
    icon: AlertOctagon,
    classes: "bg-critical/15 text-critical ring-critical/30",
  },
  high: {
    label: "High",
    icon: AlertTriangle,
    classes: "bg-high/15 text-high ring-high/30",
  },
  medium: {
    label: "Medium",
    icon: AlertCircle,
    classes: "bg-medium/15 text-medium ring-medium/30",
  },
  low: {
    label: "Low",
    icon: ShieldCheck,
    classes: "bg-low/15 text-low ring-low/30",
  },
  info: {
    label: "Info",
    icon: Info,
    classes: "bg-info/15 text-info ring-info/30",
  },
};

// Named export for backwards compatibility
export function SeverityBadge({
  severity,
  showIcon = true,
  size = "md",
  className = "",
}: SeverityBadgeProps) {
  const config = severityConfig[severity] || severityConfig.info;
  const Icon = config.icon;

  const sizeClasses = size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";
  const iconSize = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ring-1 ring-inset ${sizeClasses} ${config.classes} ${className}`}
    >
      {showIcon && <Icon className={iconSize} aria-hidden="true" />}
      {config.label}
    </span>
  );
}

// Compact indicator (for tables).
//
// Severity is encoded by SHAPE as well as colour — a square for critical, a
// diamond for high, a circle for the rest, at decreasing sizes. Colour alone
// fails for the ~1 in 12 men with a colour-vision deficiency, and red/amber is
// exactly the pair that is hardest to separate. Each also carries a text label
// for screen readers.
const DOT_SHAPE: Record<Severity, string> = {
  critical: "h-2.5 w-2.5 rounded-[2px]",
  high: "h-2.5 w-2.5 rounded-[2px] rotate-45",
  medium: "h-2.5 w-2.5 rounded-full",
  low: "h-2 w-2 rounded-full",
  info: "h-1.5 w-1.5 rounded-full",
};

const SEVERITY_BG_MAP: Record<Severity, string> = {
  critical: "bg-critical",
  high: "bg-high",
  medium: "bg-medium",
  low: "bg-low",
  info: "bg-info",
};

export function SeverityDot({ severity }: { severity: Severity }) {
  const label = severityConfig[severity]?.label || "Info";
  return (
    <span className="inline-flex items-center" role="img" aria-label={`${label} severity`}>
      <span
        className={`inline-block ${DOT_SHAPE[severity]} ${SEVERITY_BG_MAP[severity]}`}
        aria-hidden="true"
      />
    </span>
  );
}

// Severity strip (for table rows). Decorative: it always accompanies a badge
// or dot that already conveys the severity, so it is hidden from assistive
// technology rather than repeating the value.
export function SeverityStrip({ severity }: { severity: Severity }) {
  return (
    <div className={`w-1 h-full rounded-full ${SEVERITY_BG_MAP[severity]}`} aria-hidden="true" />
  );
}

// Count badge with severity color
export function SeverityCount({
  severity,
  count,
  showZero = false,
}: {
  severity: Severity;
  count: number;
  showZero?: boolean;
}) {
  if (count === 0 && !showZero) return null;

  const config = severityConfig[severity];

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${config.classes}`}
    >
      {count} {config.label}
    </span>
  );
}

// Default export for backwards compatibility
export default SeverityBadge;
